import { type FC, useEffect, type MutableRefObject, useRef, useImperativeHandle } from 'react';
import classNames from 'classnames/bind';
import Tooltip, { TooltipPreference } from '../../Tooltip/Tooltip';
import { type TextAreaPropsType } from '../types/propsType';
import { TextAreaValidations } from '../TextArea';
import styles from './ClassicTextArea.module.scss';
/**
 * @description TextArea component provides <TextArea></TextArea> with basic styling;W
 * Props ->
 * className: prop to get custom styling class
 * label: prop to add field label above text area
 * labelInfo: adds tooltip beside label, renders tooltip info if any
 * status: defines status of entry if any i.e. default,valid or invalid
 * rows: specifies the visible number of lines in a text area
 * cols: specifies the visible width of a text area
 *
 */
const cx = classNames.bind(styles);
const ClassicTextArea: FC<TextAreaPropsType> = ({
  id = '',
  className = '',
  label = '',
  labelInfo = '',
  status,
  disabled = false,
  readOnly = false,
  resize = true,
  required = false,
  message = '',
  forwardedRef,
  isSensitiveField = true,
  ...restProps
}) => {
  const { DEFAULT, VALID } = TextAreaValidations;
  const textareaRef = useRef() as MutableRefObject<HTMLTextAreaElement>;
  useImperativeHandle(forwardedRef, () => textareaRef.current);

  useEffect(() => {
    const element = textareaRef.current;
    if (element.scrollHeight > element.clientHeight) {
      element.style.height = `${element.scrollHeight}px`;
    }
  }, [restProps?.value]);
  const configs = ConfigService.getConfigs();
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;
  const CClassname = cx('textarea', { [`textarea-validated-${status}`]: true });
  const CSubClassname = cx('textarea-field-container', {
    disabled: disabled,
    'read-only': readOnly,
    resize: !resize,
  });
  return (
    <div className={CClassname}>
      {label && (
        <label htmlFor={id} className={styles['textarea-label']}>
          <span className={styles['textarea-label-text']}>{label}</span>
          {required && <sup>*</sup>}
          {labelInfo && (
            <Tooltip
              message={labelInfo}
              preference={TooltipPreference.LEFT}
              className={styles['textarea-label-tooltip']}
            />
          )}
        </label>
      )}
      <div className={CSubClassname}>
        <textarea
          id={id}
          ref={textareaRef}
          className={`${styles['textarea-field']} ${className}`}
          disabled={disabled}
          readOnly={readOnly}
          data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
          {...restProps}
        />
      </div>
      {message && (
        <span
          role="presentation"
          aria-label="message"
          aria-details={`${label} text-area`}
          className={styles['textarea-message']}
        >
          {message}
        </span>
      )}
    </div>
  );
};
ClassicTextArea.displayName = 'ClassicTextArea';

export default ClassicTextArea;
